import React, { useState } from "react";
import Grid from '@mui/material/Grid';
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import Collapse from "@mui/material/Collapse";
import FormGroup from "@mui/material/FormGroup";

import {DASHED_SEPARATOR_STYLE, PANEL_BUTTON_HEIGHT, PANEL_BUTTON_WIDTH} from "@/constants/styles";
import DividerLabel from "@/pages/custom-components/CommonComponents/DividerLabel";
import {KeyMomentSetting} from "@/types/keyMomentTypes";
import { useIntl } from "react-intl";
import {LoadingButton} from "@mui/lab";



interface KeyMomentSettingsProps {
    keyMomentOptions: KeyMomentSetting[];
    onChange: (keyMoment: string, value: boolean) => void;
    onSelectAll: (all: boolean) => void;
    col?: number;
    onSuggest: () => void;
    disableSuggest?: boolean;
    presetMode?: boolean;
    onOptionChange: (keyMoment: string, option: string, value: boolean) => void;
    loading?: boolean
}

const KeyMomentSettings: React.FC<KeyMomentSettingsProps> = ({
                                                                 onSuggest,
                                                                 disableSuggest = false,
                                                                 keyMomentOptions,
                                                                 onChange,
                                                                 onSelectAll,
                                                                 col = 3,
                                                                 presetMode = false,
                                                                 onOptionChange,
                                                                    loading = false
                                                             }) => {
    const [openCollapses, setOpenCollapses] = useState<Record<string, boolean>>({});

    const intl = useIntl();

    const toggleCollapse = (name: string) => {
        setOpenCollapses(prev => ({
            ...prev,
            [name]: !prev[name]
        }));
    };

    return (
        <Box>
            <Box marginBottom={2}>
                <DividerLabel label={"Choose key moment type"} />

                {keyMomentOptions && keyMomentOptions.length > 1 && <Box display="flex" gap={3} mt={1}>
                    <Typography
                        onClick={() => !presetMode && onSelectAll(true)}
                        sx={{
                            color: presetMode ? 'text.disabled' : 'primary.main',
                            cursor: presetMode ? 'default' : 'pointer',
                            textDecoration: 'underline',
                            textDecorationStyle: 'dashed',
                            textUnderlineOffset: '3px',
                            fontWeight: 500,
                            '&:hover': {
                                color: presetMode ? 'text.disabled' : 'primary.dark',
                            }
                        }}
                    >
                        Include All
                    </Typography>

                    <Typography
                        onClick={() => !presetMode && onSelectAll(false)}
                        sx={{
                            color: presetMode ? 'text.disabled' : 'primary.main',
                            cursor: presetMode ? 'default' : 'pointer',
                            textDecoration: 'underline',
                            textDecorationStyle: 'dashed',
                            textUnderlineOffset: '3px',
                            fontWeight: 500,
                            '&:hover': {
                                color: presetMode ? 'text.disabled' : 'primary.dark',
                            }
                        }}
                    >
                        Exclude All
                    </Typography>
                </Box>}
            </Box>

            <Grid container spacing={2}>
                <Grid size={10.5}>
                    <Grid container spacing={2}>
                        {keyMomentOptions && keyMomentOptions.map((km, index) => {
                            // Verifica se options è un oggetto schema valido e non un array vuoto
                            const hasOptions = !Array.isArray(km.options) && !!km.options.schema;
                            const isLast = index === keyMomentOptions.length - 1;

                            return (
                                <Grid size={col} key={'keyMomentOptionsGrid_' + index}>
                                    <Box
                                        sx={{
                                            height: '100%',
                                            borderRight: !isLast ? DASHED_SEPARATOR_STYLE : 'none',
                                            pr: !isLast ? 2 : 0
                                        }}
                                    >
                                        <Box sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5, px:0.5 }}>
                                                {intl.formatMessage({ id: km.name, defaultMessage: km.name })}
                                            </Typography>

                                            {/* RADIO GROUP PRINCIPALE (Include/Exclude) */}
                                            <RadioGroup
                                                value={km.selected ? 'include' : 'exclude'}
                                                onChange={(e) => onChange(km.name, e.target.value === 'include')}
                                            >
                                                <FormControlLabel
                                                    value="include"
                                                    control={<Radio color="primary" size="small" sx={{ pl: 0 , p:0.5}} />}
                                                    label="Include"
                                                    disabled={presetMode}
                                                    sx={{ ml: 0, mb: 0 }}
                                                />
                                                <FormControlLabel
                                                    value="exclude"
                                                    control={<Radio color="primary" size="small" sx={{ pl: 0, p:0.5 }} />}
                                                    label="Exclude"
                                                    disabled={presetMode}
                                                    sx={{ ml: 0 }}
                                                />
                                            </RadioGroup>

                                            {/* RENDERIZZAZIONE DINAMICA DELLE OPZIONI */}
                                            {hasOptions && (
                                                <Box mt={1}>
                                                    <Typography
                                                        variant="caption"
                                                        onClick={() => toggleCollapse(km.name)}
                                                        sx={{
                                                            color: 'primary.main',
                                                            cursor: 'pointer',
                                                            fontWeight: 'bold',
                                                            display: 'inline-block'
                                                        }}
                                                    >
                                                        Options {openCollapses[km.name] ? '▲' : '▼'}
                                                    </Typography>
                                                    <Collapse in={openCollapses[km.name]}>

                                                        {/* SCHEMA CHECKBOX */}
                                                        {(!Array.isArray(km.options) && km.options.schema === 'CheckBox') && (
                                                            <FormGroup sx={{ mt: 0.5 }}>
                                                                {km.options.values.map((opt:any) => (
                                                                    <FormControlLabel
                                                                        key={opt.name}
                                                                        control={
                                                                            <Checkbox
                                                                                color="primary"
                                                                                size="small"
                                                                                sx={{ pl: 0, p:0.5 }}
                                                                                checked={opt.value}
                                                                                onChange={(e) => onOptionChange(km.name, opt.name, e.target.checked)}
                                                                            />
                                                                        }
                                                                        label={intl.formatMessage({ id: opt.name, defaultMessage: opt.name })}
                                                                        disabled={presetMode || !km.selected}
                                                                        sx={{ ml: 0, mb: -0.5 }}
                                                                    />
                                                                ))}
                                                            </FormGroup>
                                                        )}

                                                        {/* SCHEMA RADIOBUTTON */}
                                                        {(!Array.isArray(km.options) && km.options.schema === 'RadioButton') && (() => {
                                                            // Trova l'opzione attualmente selezionata (value === true) per il RadioGroup
                                                            const selectedRadioValue = km.options.values.find(opt => opt.value)?.name || '';

                                                            return (
                                                                <Box mt={0.5}>
                                                                    <RadioGroup
                                                                        value={selectedRadioValue}
                                                                        onChange={(e) => onOptionChange(km.name, e.target.value, true)}
                                                                    >
                                                                        {km.options.values.map((opt:any) => (
                                                                            <FormControlLabel
                                                                                key={opt.name}
                                                                                value={opt.name}
                                                                                control={<Radio color="primary" size="small" sx={{ pl: 0, p:0.5 }} />}
                                                                                label={opt.label}
                                                                                disabled={presetMode || !km.selected}
                                                                                sx={{ ml: 0, mb: -0.5 }}
                                                                            />
                                                                        ))}
                                                                    </RadioGroup>
                                                                </Box>
                                                            );
                                                        })()}

                                                    </Collapse>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Grid>

                <Grid size={1.5} display={'flex'} alignItems={'center'}>
                    <LoadingButton
                        fullWidth
                        variant="contained"
                        loading={loading}
                        loadingPosition="start"
                        disabled={disableSuggest || presetMode}
                        onClick={onSuggest}
                        sx={{
                            width: PANEL_BUTTON_WIDTH + 5,
                            height: PANEL_BUTTON_HEIGHT,
                        }}
                    >
                        Get Key Moments
                    </LoadingButton>
                </Grid>
            </Grid>
        </Box>
    );
};

export default KeyMomentSettings;